import { Injectable } from '@angular/core';
import { BaseFacade } from '../base/base.facade';
import { SiteSettingStore } from './site-setting.store';
import { SiteSettingQuery } from './site-setting.query';
import { Observable, forkJoin, of } from 'rxjs';
import { SiteSettingService } from './site-setting.service';
import { map, flatMap, find } from 'rxjs/operators';
import { SiteSettingModel } from './site-setting.model';
import { TranslateService } from '@ngx-translate/core';
import { DynamicSchemaFactory } from '../dynamic-schema.factory';
import { AppToken } from "../../../models/app-token";
import { LanguageModel } from "../language-schema/language.model";
import { LanguageFacade } from '../language-schema/language.facade';

@Injectable({ providedIn: 'root' })
export class SiteSettingFacade extends BaseFacade {

  constructor(
    private readonly siteSettingService: SiteSettingService,
    siteSettingStore: SiteSettingStore,
    siteSettingQuery: SiteSettingQuery,
    dynamicSchemaFacto: DynamicSchemaFactory,
    private languageFacade: LanguageFacade,
    translateService: TranslateService) {
    super(siteSettingService,
      siteSettingStore,
      siteSettingQuery,
      dynamicSchemaFacto.getDynamicSchemaFacade(AppToken.SITE_SETTING_SCHEMA_NAME),
      translateService,
      AppToken.SITE_SETTING_SCHEMA_NAME + '-facade'
      );
  }

  private initializeSiteSetting(): Observable<SiteSettingModel> {
    return forkJoin(
      this.siteSettingService.getFirstContent(),
      this.getSchema())
      .pipe(
        map(([firstContent, schema]) => {
          this.getStore().add(firstContent);
          this.getStore().update({ totalCount: 1 });
          this.getStore().update({ schema: schema });
          return firstContent;
        })
      );
  }

  selectFirst(): Observable<SiteSettingModel> {
    if (!this.getQuery().hasEntity()) {
      return this.initializeSiteSetting();
    } else {
      return this.getQuery().selectFirst();
    }
  }

  getSiteLanguages(siteSettingModel: SiteSettingModel = null): Observable<LanguageModel[]> {
    if (siteSettingModel) {
      return this.languageFacade.getLanguages(siteSettingModel.data.language.iv, 'data/index/iv');
    }
    const languages$ = this.getValidLanguage();
    return languages$.pipe(flatMap(languages => {
      return this.languageFacade.getLanguages(languages, 'data/index/iv');
    }));
  }

  getValidLanguage(): Observable<string[]> {
    return this.selectFirst().pipe(map(siteSetting => {
      return siteSetting.data.language.iv;
    }));
  }

  getSiteDefaultLanguage(): Observable<LanguageModel> {
    return this.getSiteLanguages()
      .pipe(map(languages => languages
        .find(language => language.data.isdefault && language.data.isdefault.iv === true)));
  }
}
